import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  MenuItem,
  Divider,
  CircularProgress,
  Tabs,
  Tab,
  Autocomplete,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import userService from "../../services/userService";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 20 }, (_, i) => CURRENT_YEAR + 1 - i);

const COLORS = [
  "White",
  "White Pearl",
  "Black",
  "Jet Black Metallic",
  "Silver",
  "Grey",
  "Sonic Grey Pearl",
  "Red",
  "Blue",
  "Navy Blue",
  "Antimatter Blue",
  "Green",
  "Brown",
  "Beige",
  "Orange",
  "Other",
];

const COUNTRIES = [
  "USA",
  "UK",
  "Canada",
  "Germany",
  "Japan",
  "South Korea",
  "China",
  "Other",
];

const blank = {
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  make: "",
  model: "",
  year: CURRENT_YEAR,
  color: "",
  engineNo: "",
  chassisNo: "",
  originCountry: "USA",
  originPort: "",
  dealerName: "",
  vesselName: "",
  billOfLading: "",
  containerNumber: "",
  bookingReference: "",
  departureDate: "",
  estimatedArrivalDate: "",
  declaredValue: "",
  isPublic: true,
};

const Field = ({
  label,
  field,
  value,
  onChange,
  type = "text",
  required,
  helperText,
  select,
  children,
  placeholder,
  inputProps,
  slotProps,
}) => (
  <TextField
    label={label}
    type={type}
    value={value}
    onChange={onChange}
    size="small"
    fullWidth
    required={required}
    helperText={helperText}
    select={select}
    placeholder={placeholder}
    inputProps={inputProps}
    slotProps={slotProps}
  >
    {children}
  </TextField>
);

const VehicleFormDialog = ({
  open,
  onClose,
  onSubmit,
  initial = null,
  loading = false,
}) => {
  const isEdit = !!initial;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(blank);

  // Customer lookup (debounced server search, same pattern as ShipmentForm)
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState("");

  useEffect(() => {
    if (!open || isEdit) return;

    const query = customerInput.trim();
    if (query.length < 2) {
      setCustomerOptions([]);
      return;
    }

    setCustomerLoading(true);
    const timer = setTimeout(() => {
      userService
        .getAll({ role: "customer", search: query, limit: 20 })
        .then(({ data }) => setCustomerOptions(data.data || []))
        .catch(() => setCustomerOptions([]))
        .finally(() => setCustomerLoading(false));
    }, 1000);

    return () => clearTimeout(timer);
  }, [customerInput, open, isEdit]);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setCustomerInput("");
      return;
    }
    if (initial) {
      setForm({
        customerName: initial.customerName || "",
        customerPhone: initial.customerPhone || "",
        customerEmail: initial.customerEmail || "",
        make: initial.vehicle?.make || "",
        model: initial.vehicle?.model || "",
        year: initial.vehicle?.year || CURRENT_YEAR,
        color: initial.vehicle?.color || "",
        engineNo: initial.vehicle?.engineNo || "",
        chassisNo: initial.vehicle?.chassisNo || "",
        originCountry: initial.origin?.country || "USA",
        originPort: initial.origin?.port || "",
        dealerName: initial.origin?.dealerName || "",
        vesselName: initial.shipping?.vesselName || "",
        billOfLading: initial.shipping?.billOfLading || "",
        containerNumber: initial.shipping?.containerNumber || "",
        bookingReference: initial.shipping?.bookingReference || "",
        departureDate: initial.shipping?.departureDate
          ? initial.shipping.departureDate.slice(0, 10)
          : "",
        estimatedArrivalDate: initial.shipping?.estimatedArrivalDate
          ? initial.shipping.estimatedArrivalDate.slice(0, 10)
          : "",
        declaredValue: initial.declaredValue || "",
        isPublic: initial.isPublic !== false,
      });
    } else {
      setForm(blank);
    }
  }, [initial, open]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleCustomerSelect = (val) => {
    setSelectedCustomer(val);
    if (val) {
      setForm((p) => ({
        ...p,
        customerName: `${val.firstName} ${val.lastName}`.trim(),
        customerEmail: val.email || "",
        customerPhone: val.phone || "",
      }));
    }
  };

  const handleSubmit = () => {
    const payload = {
      customerName: form.customerName,
      customerPhone: form.customerPhone,
      customerEmail: form.customerEmail,
      customer: selectedCustomer?._id || initial?.customer || undefined,
      vehicle: {
        make: form.make,
        model: form.model,
        year: Number(form.year),
        color: form.color,
        engineNo: form.engineNo,
        chassisNo: form.chassisNo,
      },
      origin: {
        country: form.originCountry,
        port: form.originPort,
        dealerName: form.dealerName,
      },
      shipping: {
        vesselName: form.vesselName,
        billOfLading: form.billOfLading,
        containerNumber: form.containerNumber,
        bookingReference: form.bookingReference,
        departureDate: form.departureDate || undefined,
        estimatedArrivalDate: form.estimatedArrivalDate || undefined,
      },
      declaredValue: form.declaredValue ? Number(form.declaredValue) : 0,
      isPublic: form.isPublic,
    };
    onSubmit(payload);
  };

  const canSubmit = form.make && form.model && form.year && form.customerName;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={700} fontSize={16}>
          {isEdit ? "Edit Vehicle Shipment" : "New Vehicle Shipment"}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 2,
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          <Tab label="Customer" />
          <Tab label="Vehicle" />
          <Tab label="Origin & Shipping" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 3 }}>
        {/* ── TAB 0: Customer ─────────────────────────────────── */}
        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Customer Information
            </Typography>

            {!isEdit && (
              <Autocomplete
                options={customerOptions}
                loading={customerLoading}
                value={selectedCustomer}
                onChange={(_, val) => handleCustomerSelect(val)}
                inputValue={customerInput}
                onInputChange={(_, val) => setCustomerInput(val)}
                filterOptions={(x) => x}
                getOptionLabel={(o) =>
                  o ? `${o.firstName} ${o.lastName} (${o.email})` : ""
                }
                isOptionEqualToValue={(o, v) => o._id === v._id}
                noOptionsText={
                  customerInput.trim().length < 2
                    ? "Type at least 2 characters…"
                    : customerLoading
                      ? "Searching…"
                      : "No customers found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Find existing customer (optional)"
                    placeholder="Search by name, email, or TIN…"
                    helperText="Selecting a customer auto-fills the fields below — you can still edit them after"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps?.input,
                        endAdornment: (
                          <>
                            {customerLoading && <CircularProgress size={16} />}
                            {params.slotProps?.input?.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}

            <Field
              label="Customer Full Name *"
              field="customerName"
              value={form.customerName}
              onChange={set("customerName")}
              required
            />
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <Field
                label="Phone Number"
                field="customerPhone"
                value={form.customerPhone}
                onChange={set("customerPhone")}
                type="tel"
              />
              <Field
                label="Email Address"
                field="customerEmail"
                value={form.customerEmail}
                onChange={set("customerEmail")}
                type="email"
              />
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <Field
                label="Declared Value (USD)"
                field="declaredValue"
                value={form.declaredValue}
                onChange={set("declaredValue")}
                type="number"
                helperText="Vehicle purchase price"
                inputProps={{ min: 0 }}
              />
              <Field
                select
                label="Public Tracking"
                value={form.isPublic ? "yes" : "no"}
                onChange={(e) =>
                  setForm((p) => ({ ...p, isPublic: e.target.value === "yes" }))
                }
                size="small"
                helperText="Allow customer to look up by tracking ID"
              >
                <MenuItem value="yes">Yes — tracking page enabled</MenuItem>
                <MenuItem value="no">No — private only</MenuItem>
              </Field>
            </Box>
          </Box>
        )}

        {/* ── TAB 1: Vehicle ──────────────────────────────────── */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Vehicle Details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 140px" },
                gap: 2,
              }}
            >
              <Field
                label="Make *"
                field="make"
                value={form.make}
                onChange={set("make")}
                required
                placeholder="e.g. Toyota"
              />
              <Field
                label="Model *"
                field="model"
                value={form.model}
                onChange={set("model")}
                required
                placeholder="e.g. Land Cruiser"
              />
              <TextField
                select
                label="Year *"
                value={form.year}
                onChange={set("year")}
                size="small"
                required
              >
                {YEARS.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <TextField
                select
                label="Color"
                value={form.color}
                onChange={set("color")}
                size="small"
              >
                <MenuItem value="">
                  <em>Select color…</em>
                </MenuItem>
                {COLORS.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <Field
                label="Engine Number"
                field="engineNo"
                value={form.engineNo}
                onChange={set("engineNo")}
              />
              <Field
                label="Chassis Number"
                field="chassisNo"
                value={form.chassisNo}
                onChange={set("chassisNo")}
              />
            </Box>
          </Box>
        )}

        {/* ── TAB 2: Origin & Shipping ─────────────────────────── */}
        {tab === 2 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Origin
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                select
                label="Country"
                value={form.originCountry}
                onChange={set("originCountry")}
                size="small"
              >
                {COUNTRIES.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
              <Field
                label="Port of Origin"
                field="originPort"
                value={form.originPort}
                onChange={set("originPort")}
                placeholder="e.g. Port of Miami"
              />
              <Field
                label="Dealer / Seller Name"
                field="dealerName"
                value={form.dealerName}
                onChange={set("dealerName")}
                placeholder="e.g. AutoNation Toyota"
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />
            <Typography variant="overline" color="text.secondary">
              Shipping Details
            </Typography>

            <Box
              sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}
            >
              <Field
                label="Vessel Name"
                field="vesselName"
                value={form.vesselName}
                onChange={set("vesselName")}
                placeholder="e.g. MV Cayman Voyager"
              />
              <Field
                label="Bill of Lading #"
                field="billOfLading"
                value={form.billOfLading}
                onChange={set("billOfLading")}
                placeholder="e.g. BL-2026-00441"
              />
              <Field
                label="Container Number"
                field="containerNumber"
                value={form.containerNumber}
                onChange={set("containerNumber")}
                placeholder="e.g. TCKU3456789"
              />
              <Field
                label="Booking Reference"
                field="bookingReference"
                value={form.bookingReference}
                onChange={set("bookingReference")}
                placeholder="Optional"
              />
              <Field
                label="Departure Date"
                field="departureDate"
                value={form.departureDate}
                onChange={set("departureDate")}
                type="date"
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  "& .MuiInputLabel-root": {
                    backgroundColor: "background.paper",
                    px: 0.5,
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />
              <Field
                label="Estimated Arrival"
                field="estimatedArrivalDate"
                value={form.estimatedArrivalDate}
                onChange={set("estimatedArrivalDate")}
                type="date"
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{
                  "& .MuiInputLabel-root": {
                    backgroundColor: "background.paper",
                    px: 0.5,
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Box sx={{ display: "flex", gap: 1, mr: "auto" }}>
          {tab > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTab((t) => t - 1)}
            >
              ← Previous
            </Button>
          )}
          {tab < 2 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTab((t) => t + 1)}
            >
              Next →
            </Button>
          )}
        </Box>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || loading}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Shipment"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleFormDialog;